<script lang="ts">
	import type { OrderData, PaymentData } from "@hitit/api/src/types";
	//UI
	//Utils
	import { t, api } from "@hitit/hub/src/stores";
	import { formatKrwNumber } from "@hitit/hub/src/v7/utils";
	import moment from "moment";

	//Type
	import type { AxiosResponse } from "axios";
	//Image
	import CASH_CANCELED from "@hitit/hub/src/assets/Icons/order_cancel_cash.png";
	import COMPLEX_CANCELED from "@hitit/hub/src/assets/Icons/order_cancel_complex.png";
	import CARD_CANCELED from "@hitit/hub/src/assets/Icons/order_cancel_card.png";
	import CARD from "@hitit/hub/src/assets/Icons/order_card.png";
	import CASH from "@hitit/hub/src/assets/Icons/order_cash.png";
	import COMPLEX from "@hitit/hub/src/assets/Icons/order_complex.png";
	import UNKNOWN from "@hitit/hub/src/assets/Icons/order_unknown.png";
	import UNKNOWN_CANCEL from "@hitit/hub/src/assets/Icons/open_cancel.png";
	import UNKNOWN_CLOSE from "@hitit/hub/src/assets/Icons/close_unknown.png";
	import OrderDetail from "./OrderDetail.svelte";
	import { DefaultLoading } from "@hitit/new_ui";

	type OrderResponse = Promise<AxiosResponse<{ orderDTO: OrderData }>>;
	type PaymentResponse = Promise<AxiosResponse<{ paymentDTOs: PaymentData[] }>>;

	export let storeId: string;
	export let orderId: string;

	let order: OrderResponse;
	let payment: PaymentResponse;

	function pickStatusImageURL(status = "", paymentMethod = "") {
		if (status === "CANCELLED") {
			// CANCELLED
			if (paymentMethod === "INSTORE_CAT") {
				return CARD_CANCELED;
			} else if (paymentMethod === "INSTORE_CASH") {
				return CASH_CANCELED;
			} else if (paymentMethod === "COMPLEX") {
				return COMPLEX_CANCELED;
			} else {
				return UNKNOWN_CANCEL;
			}
		} else if (status === "OPEN") {
			//
			return UNKNOWN;
		} else {
			// CLOSED
			if (paymentMethod === "INSTORE_CAT") {
				return CARD;
			} else if (paymentMethod === "INSTORE_CASH") {
				return CASH;
			} else if (paymentMethod === "COMPLEX") {
				return COMPLEX;
			} else {
				return UNKNOWN_CLOSE;
			}
		}

		// CLOSED
	}

	function processingDate(time: string | undefined) {
		if (!time) return "";
		return !time
			? ""
			: moment(new Date(time + "Z").toISOString()).format("yyyy.MM.DD HH:mm");
	}

	function itemSummaryGenerate(orderDTO: OrderData) {
		let summary = "";
		const sorted = orderDTO.itemDTOs?.sort(
			(a, b) => a.displaySort! - b.displaySort!
		);
		sorted?.forEach((e, i) => {
			i !== 0 ? (summary += ` , ${e.name}`) : (summary += e.name);
		});
		return summary;
	}

	function loadData(orderId: string, storeId: string) {
		try {
			payment = $api.listOrderPayments(orderId);
			order = $api.getOrder(storeId, orderId);
		} catch (error) {
			console.log(error);
		}
	}

	$: loadData(orderId, storeId);
</script>

<div class="container" t-id="OrderDetailLink">
	<div class="head">
		<div class="col-header status">{$t("hub.order.order_status")}</div>
		<div class="col-header id">{$t("hub.order.order_number")}</div>
		<div class="col-header items">{$t("hub.order.order_contents")}</div>
		<div class="col-header price">{$t("hub.order.item_amount")}</div>
		<div class="col-header createdate">{$t("hub.order.order_date")}</div>
	</div>
	<div class="body">
		<div class="row">
			{#await order}
				<div class="loading">
					<DefaultLoading />
				</div>
			{:then order}
				<div class="order-summary status">
					<img
						src={pickStatusImageURL(
							order.data.orderDTO.orderStatus,
							order.data.orderDTO.orderPaymentMethod
						)}
						alt=""
					/>
				</div>
				<div class="order-summary id">
					<p>{order.data.orderDTO.shortid}</p>
				</div>
				<div class="order-summary items">
					<p>{itemSummaryGenerate(order.data.orderDTO)}</p>
				</div>
				<div class="order-summary price">
					<p>₩{formatKrwNumber(order.data.orderDTO.grandTotalPrice)}</p>
				</div>
				<div class="order-summary createdate">
					<p>{processingDate(order.data.orderDTO.createdDate)}</p>
				</div>
			{/await}
		</div>
		<OrderDetail orderResponse={order} paymentResponse={payment} />
	</div>
</div>

<style lang="scss">
	@import "../../../variables.scss";
	div {
		box-sizing: border-box;
	}
	.container {
		width: 100%;

		.head {
			display: none;
		}

		.body {
			.row {
				padding: 20px 10px 20px 20px;
				gap: 10px 10px;
				display: flex;
				flex-wrap: wrap;
				position: relative;

				&:not(:last-child)::after {
					content: "";
					display: block;
					margin-top: 20px;
					width: calc(100% - 20px);
					height: 1px;
					background: #e6e7ec;
					position: absolute;
					bottom: 0;
					left: 50%;
					transform: translate(-50%);
				}

				&.showDetail::after {
					height: 0;
					background-color: transparent;
				}

				.price {
					font-weight: 550;
					color: #46474b;
				}
				.createdate {
					color: #6d6f76;
				}
			}

			p {
				width: 100%;
				overflow: hidden;
				white-space: nowrap;
				text-overflow: ellipsis;
				text-align: left;
				letter-spacing: -0.8px;
			}
		}
		.order-summary {
			display: flex;
			overflow: hidden;
			justify-content: center;
			align-items: center;
			overflow-x: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
		.status {
			img {
				width: 30px;
				height: 22.2px;
			}
		}

		.id {
			display: none;
		}

		.storename {
			display: none;
			.allStoreMode {
				display: flex;
			}
		}

		.items {
			flex-basis: calc(100% - 40px);
		}

		.price {
			flex-basis: calc(100% - 110px);
			padding-left: 40px;
		}

		.createdate {
			font-size: 0.875rem;
			min-width: 100px;
			width: 100px;
			color: #6d6f76;
		}
	}

	@include DesktopSize {
		.container {
			.head {
				padding: 30px 30px;
				background-color: white;
			}

			.head {
				display: flex;
				justify-content: space-between;

				.col-header {
					font-size: 1.25rem;
					color: #6d6f76;
					text-align: center;
				}
			}

			.status {
				width: 75px;
				min-width: 75px;
			}

			.id {
				//260
				display: flex;
				flex-grow: 260;
				max-width: calc((100% - 75px - 150px - 60px) * (260 / 1564));
				min-width: 120px;

				justify-content: center;
			}

			.storename {
				display: none;
				width: 150px;
				min-width: 150px;
				max-width: 150px;

				&.allStoreMode {
					display: block;

					& ~ .id {
						//260
						flex-grow: 260;
						max-width: calc((100% - 75px - 150px - 60px) * (260 / 1564));
						min-width: 120px;
					}

					& ~ .items {
						width: calc((100% - 150px - 75px - 150px - 120px) * 0.65);
						max-width: calc((100% - 150px - 75px - 150px - 120px) * 0.65);
					}

					& ~ .price {
						width: calc((100% - 150px - 75px - 150px - 120px) * 0.35);
						max-width: calc((100% - 150px - 75px - 150px - 120px) * 0.35);
					}
				}
			}

			.items {
				flex-grow: 900;
				max-width: calc((100% - 75px - 150px - 60px) * (900 / 1564));
			}

			.price {
				padding-left: 0px;
				flex-grow: 404;
				max-width: calc((100% - 75px - 150px - 60px) * (404 / 1564));
			}

			.createdate {
				min-width: 150px;
				width: 150px;
			}
		}

		.body {
			width: 100%;
			.row {
				flex-wrap: wrap;
				justify-content: space-between;
				width: 100%;
				gap: 0 !important;
				padding: 0px 30px 20px 30px !important;
				display: flex;
				background-color: white;
				&:not(:last-child)::after {
					width: 100%;
				}

				&:not(.showDetail):not(:last-child)::after {
					content: "";
					display: block;
					width: 100%;
					height: 1px;
					background: #e6e7ec;
				}

				.order-summary {
					display: flex;
					overflow: hidden;
					justify-content: center;
					align-items: center;
					overflow-x: hidden;
					white-space: nowrap;
					text-overflow: ellipsis;
					font-size: 1.25rem;
					color: #46474b !important;

					&.price {
						font-weight: 200;
					}

					&.storename:not(.allStoreMode) {
						display: none;
					}

					&.id {
						display: flex;
					}
				}

				.status {
					img {
						width: 47.2px;
						height: 35.5px;
					}
				}

				p {
					width: 100%;
					overflow: hidden;
					white-space: nowrap;
					text-overflow: ellipsis;
					text-align: center;
					letter-spacing: -0.8px;
				}
			}
		}
	}
</style>
